import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MailIcon, MapPinIcon, ClockIcon, ArrowRightIcon, CheckCircle2Icon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
export function ContactCTA() {
  const {
    t,
    language
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subjectBase = language === 'es' ? 'Nueva consulta desde DAB Consulting' : 'New Inquiry from DAB Consulting';
    const subject = `${subjectBase} — ${formData.name || 'Website Lead'}`;
    const labels = language === 'es' ? {
      name: 'Nombre',
      email: 'Correo',
      company: 'Empresa',
      service: 'Servicio de interés',
      message: 'Mensaje'
    } : {
      name: 'Name',
      email: 'Email',
      company: 'Company',
      service: 'Service Interest',
      message: 'Message'
    };
    const body = [`${labels.name}: ${formData.name}`, `${labels.email}: ${formData.email}`, `${labels.company}: ${formData.company || '—'}`, `${labels.service}: ${formData.service || '—'}`, '', `${labels.message}:`, formData.message].join('\n');
    const mailtoUrl = `mailto:hello@dabconsultingusa.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 6000);
  };
  return <section id="contact" className="bg-white" data-id="element-36">
      {/* Gradient Banner */}
      <div className="bg-gradient-to-r from-electric-700 via-electric to-electric-light py-20 relative overflow-hidden" data-id="element-37">
        {/* Abstract shapes */}
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-38" />
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-navy opacity-20 rounded-full blur-3xl" data-id="element-39" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-40">
          <motion.h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-41">
            {t('contact_banner_title')}
          </motion.h2>
          <motion.p className="text-xl text-electric-50 mb-10 max-w-2xl mx-auto" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} data-id="element-42">
            {t('contact_banner_desc')}
          </motion.p>
        </div>
      </div>

      {/* Form & Info Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-43">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8" data-id="element-44">
          {/* Contact Info */}
          <div className="lg:col-span-1 flex flex-col gap-8" data-id="element-45">
            <div data-id="element-46">
              <h3 className="text-2xl font-bold text-navy mb-6" data-id="element-47">
                {t('contact_info_title')}
              </h3>
              <p className="text-slate-600 mb-8" data-id="element-48">{t('contact_info_desc')}</p>
            </div>

            <div className="flex items-start gap-4" data-id="element-49">
              <div className="w-12 h-12 rounded-lg bg-electric/10 flex items-center justify-center flex-shrink-0" data-id="element-50">
                <PhoneIcon className="w-6 h-6 text-electric" data-id="element-51" />
              </div>
              <div data-id="element-52">
                <div className="font-bold text-navy mb-1" data-id="element-53">
                  {t('contact_phone')}
                </div>
                <a href="tel:+17282095124" className="text-slate-600 hover:text-electric transition-colors" data-id="element-54">
                  +1 (728) 209-5124
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-55">
              <div className="w-12 h-12 rounded-lg bg-electric/10 flex items-center justify-center flex-shrink-0" data-id="element-56">
                <MailIcon className="w-6 h-6 text-electric" data-id="element-57" />
              </div>
              <div data-id="element-58">
                <div className="font-bold text-navy mb-1" data-id="element-59">
                  {t('contact_email')}
                </div>
                <a href="mailto:hello@dabconsultingusa.com" className="text-slate-600 hover:text-electric transition-colors" data-id="element-60">
                  hello@dabconsultingusa.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-61">
              <div className="w-12 h-12 rounded-lg bg-electric/10 flex items-center justify-center flex-shrink-0" data-id="element-62">
                <MapPinIcon className="w-6 h-6 text-electric" data-id="element-63" />
              </div>
              <div data-id="element-64">
                <div className="font-bold text-navy mb-1" data-id="element-65">
                  {t('contact_hq')}
                </div>
                <address className="text-slate-600 not-italic" data-id="element-66">
                  500 S Australian Ave
                  <br data-id="element-67" />
                  Suite 600
                  <br data-id="element-68" />
                  West Palm Beach, FL 33407
                </address>
              </div>
            </div>

            <div className="flex items-start gap-4" data-id="element-69">
              <div className="w-12 h-12 rounded-lg bg-electric/10 flex items-center justify-center flex-shrink-0" data-id="element-70">
                <ClockIcon className="w-6 h-6 text-electric" data-id="element-71" />
              </div>
              <div data-id="element-72">
                <div className="font-bold text-navy mb-1" data-id="element-73">
                  {t('contact_hours')}
                </div>
                <div className="text-slate-600" data-id="element-74">{t('contact_hours_val')}</div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2" data-id="element-75">
            <motion.form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 shadow-xl shadow-slate-200/50" initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-76">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-77">
                <div data-id="element-78">
                  <label htmlFor="name" className="block text-sm font-semibold text-navy mb-2" data-id="element-79">
                    {t('form_name')}
                  </label>
                  <input type="text" id="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-electric focus:border-electric outline-none transition-all bg-slate-50 focus:bg-white" placeholder={t('form_name_ph')} required data-id="element-80" />
                </div>
                <div data-id="element-81">
                  <label htmlFor="email" className="block text-sm font-semibold text-navy mb-2" data-id="element-82">
                    {t('form_email')}
                  </label>
                  <input type="email" id="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-electric focus:border-electric outline-none transition-all bg-slate-50 focus:bg-white" placeholder={t('form_email_ph')} required data-id="element-83" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-84">
                <div data-id="element-85">
                  <label htmlFor="company" className="block text-sm font-semibold text-navy mb-2" data-id="element-86">
                    {t('form_company')}
                  </label>
                  <input type="text" id="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-electric focus:border-electric outline-none transition-all bg-slate-50 focus:bg-white" placeholder={t('form_company_ph')} data-id="element-87" />
                </div>
                <div data-id="element-88">
                  <label htmlFor="service" className="block text-sm font-semibold text-navy mb-2" data-id="element-89">
                    {t('form_service')}
                  </label>
                  <select id="service" value={formData.service} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-electric focus:border-electric outline-none transition-all bg-slate-50 focus:bg-white text-slate-700" data-id="element-90">
                    <option value="" data-id="element-91">{t('form_service_ph')}</option>
                    <option value={t('service_1_title')} data-id="element-92">
                      {t('service_1_title')}
                    </option>
                    <option value={t('service_2_title')} data-id="element-93">
                      {t('service_2_title')}
                    </option>
                    <option value={t('service_3_title')} data-id="element-94">
                      {t('service_3_title')}
                    </option>
                    <option value={t('service_4_title')} data-id="element-95">
                      {t('service_4_title')}
                    </option>
                    <option value={t('service_5_title')} data-id="element-96">
                      {t('service_5_title')}
                    </option>
                    <option value={t('service_6_title')} data-id="element-97">
                      {t('service_6_title')}
                    </option>
                  </select>
                </div>
              </div>

              <div className="mb-8" data-id="element-98">
                <label htmlFor="message" className="block text-sm font-semibold text-navy mb-2" data-id="element-99">
                  {t('form_msg')}
                </label>
                <textarea id="message" rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-electric focus:border-electric outline-none transition-all bg-slate-50 focus:bg-white resize-none" placeholder={t('form_msg_ph')} required data-id="element-100"></textarea>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-101">
                <button type="submit" className="w-full sm:w-auto inline-flex justify-center items-center gap-2 bg-electric hover:bg-electric-hover text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-lg shadow-electric/30 hover:-translate-y-1" data-id="element-102">
                  {t('form_submit')}
                  <ArrowRightIcon className="w-5 h-5" data-id="element-103" />
                </button>

                {submitted && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex items-center gap-2 text-sm font-medium text-emerald-600" data-id="element-104">
                    <CheckCircle2Icon className="w-5 h-5" data-id="element-105" />
                    {language === 'es' ? 'Abriendo tu correo… revisa tu cliente de email.' : 'Opening your email… check your mail client.'}
                  </motion.div>}
              </div>
            </motion.form>
          </div>
        </div>
      </div>
    </section>;
}